import React, { memo } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import ShareButton from './ShareButton';
import * as FiIcons from 'react-icons/fi';

const { FiRefreshCw, FiCloud, FiAlertTriangle, FiClock, FiCheckCircle, FiActivity, FiMap, FiEyeOff, FiMapPin, FiShield } = FiIcons;

const WeatherHeader = memo(({ lastUpdated, onRefresh, loading, error, fetchAttempts, hasTyphoon, onToggleMap, showMap, weatherData, typhoonData }) => {
  const formatDateTime = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-PH', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date) + ' PHT';
  };

  const getNextUpdateTime = () => {
    if (!lastUpdated) return '';
    const nextUpdate = new Date(lastUpdated.getTime() + (30 * 60 * 1000));
    return formatDateTime(nextUpdate);
  };

  const getStatusColor = () => {
    if (hasTyphoon) return 'text-orange-600';
    if (error) return 'text-red-600';
    return 'text-green-600';
  };

  const getStatusIcon = () => {
    if (hasTyphoon) return FiAlertTriangle;
    if (error) return FiAlertTriangle;
    return FiCheckCircle;
  };

  const getStatusText = () => {
    if (hasTyphoon) return 'Active Typhoon';
    if (error) return 'Fallback Mode';
    return 'All Systems OK';
  };

  return (
    <>
      <div className="sr-only">
        <h1>Iligan City Weather Report and Typhoon Tracker</h1>
        <p>
          Official weather data provided by PAGASA (Philippine Atmospheric, Geophysical and Astronomical Services Administration).
          Real-time updates for Iligan City and Northern Mindanao.
        </p>
      </div>

      <motion.header 
        initial={{ opacity: 0, y: -20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100 relative overflow-hidden" 
        role="banner"
      >
        {/* PAGASA Official Ribbon */}
        <div className="absolute top-0 right-0">
          <div className="bg-blue-600 text-white text-[10px] font-bold px-8 py-1 transform rotate-45 translate-x-10 translate-y-2 shadow-sm">
            OFFICIAL PAGASA DATA
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <SafeIcon icon={FiCloud} className="text-2xl text-blue-600" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                  <SafeIcon icon={FiMapPin} className="mr-2 text-blue-600" /> 
                  Iligan Weather Tracker
                </h1>
                <span className="hidden md:inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                  <SafeIcon icon={FiShield} className="mr-1 text-[10px]" /> Verified PAGASA Source
                </span>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 text-sm text-gray-600">
                <span className="text-blue-700 font-medium">📍 Iligan City, Northern Mindanao</span>
                <span className="hidden sm:inline">•</span>
                <time dateTime={new Date().toISOString()}>
                  Current: {formatDateTime(new Date())}
                </time>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className={`flex items-center ${getStatusColor()}`}>
              <SafeIcon icon={getStatusIcon()} className="mr-2" />
              <span className="font-medium">{getStatusText()}</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <ShareButton weatherData={weatherData} typhoonData={typhoonData} location="Iligan City" size="default" />
              <motion.button 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }} 
                onClick={onRefresh} 
                disabled={loading} 
                className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg transition-colors shadow-md"
                aria-label={loading ? 'Updating weather data' : 'Refresh weather data'}
              >
                <SafeIcon icon={FiRefreshCw} className={loading ? 'animate-spin' : ''} />
                <span className="font-medium">
                  {loading ? 'Updating...' : 'Sync with PAGASA'}
                </span>
              </motion.button>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="flex flex-wrap items-center justify-between text-sm text-gray-500 gap-3">
            <div className="flex items-center space-x-6">
              <span className="flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                Official Data Feed: <strong className="ml-1 text-gray-700">PAGASA Synoptic</strong>
              </span>
              {lastUpdated && (
                <span className="flex items-center">
                  <SafeIcon icon={FiClock} className="mr-1" />
                  Last Updated: {formatDateTime(lastUpdated)}
                </span>
              )}
            </div>
            <div className="text-xs italic text-gray-400">
              Data synchronized via PAGASA Open Data Initiative
            </div>
          </div>
        </div>
      </motion.header>
    </>
  );
});

WeatherHeader.displayName = 'WeatherHeader';
export default WeatherHeader;